import React, { useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Stack,
  Typography,
} from '@mui/material'
import ThumbUpOutlined from '@mui/icons-material/ThumbUpOutlined'
import ThumbDownOutlined from '@mui/icons-material/ThumbDownOutlined'
import ContentCopy from '@mui/icons-material/ContentCopy'
import { disputeReviewAPI } from '../services/api'

const OVERALL = {
  clean: { label: 'Clean', color: 'success' },
  minor_issues: { label: 'Minor issues', color: 'warning' },
  significant_issues: { label: 'Significant issues', color: 'error' },
}

const SEVERITY_COLOR = { high: 'error', medium: 'warning', low: 'info' }
const SEVERITY_ORDER = ['high', 'medium', 'low']

const FAULT_LABELS = {
  model: "Model didn't follow its prompt",
  prompt: 'Prompt gap',
  data_or_tool: 'Tool or data problem',
}

const STATUS_NOTES = {
  skipped_no_prompt_snapshot: "Not reviewed by AI: this ticket predates saved prompts. Rule checks only.",
  skipped_too_large: 'Not reviewed by AI: the ticket is too large. Rule checks only.',
  invalid_output: 'The AI reviewer returned unusable output for this ticket. Rule checks only.',
  error: 'The AI reviewer failed on this ticket. Rule checks only.',
}

function FindingCard({ finding, agentName, ticketId, reviewedAt }) {
  const [feedback, setFeedback] = useState(finding.feedback?.verdict || null)
  const [saving, setSaving] = useState(false)
  const [failed, setFailed] = useState(null)

  const send = async (verdict) => {
    setSaving(true)
    setFailed(null)
    try {
      await disputeReviewAPI.sendFeedback(ticketId, finding.id, verdict, reviewedAt)
      setFeedback(verdict)
    } catch (err) {
      setFailed(err?.response?.status === 409 ? 'Re-reviewed since you opened it. Reopen to see the latest.' : "Couldn't save.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }} data-testid="review-finding">
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Chip size="small" label={finding.severity} color={SEVERITY_COLOR[finding.severity] || 'default'} />
        <Typography variant="subtitle2">{finding.summary}</Typography>
        <Chip size="small" variant="outlined" label={finding.source === 'rule' ? 'Rule check' : 'AI review'} />
        {finding.fault && <Chip size="small" variant="outlined" label={FAULT_LABELS[finding.fault] || finding.fault} />}
      </Box>
      <Typography variant="caption" color="text.secondary">
        {agentName}
        {finding.message_i !== null && finding.message_i !== undefined && ` · message #${finding.message_i}`}
      </Typography>
      {finding.evidence_quote && (
        <Typography variant="body2" sx={{ my: 0.5, pl: 1, borderLeft: 3, borderColor: 'divider', fontStyle: 'italic' }}>
          “{finding.evidence_quote}”
        </Typography>
      )}
      {finding.detail && <Typography variant="body2">{finding.detail}</Typography>}
      {finding.suggested_fix && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          <strong>Suggested fix:</strong> {finding.suggested_fix}
        </Typography>
      )}
      {finding.source === 'llm' && (
        <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<ThumbUpOutlined fontSize="small" />}
            variant={feedback === 'agree' ? 'contained' : 'text'}
            disabled={saving}
            onClick={() => send('agree')}
          >
            Agree
          </Button>
          <Button
            size="small"
            startIcon={<ThumbDownOutlined fontSize="small" />}
            variant={feedback === 'disagree' ? 'contained' : 'text'}
            disabled={saving}
            onClick={() => send('disagree')}
          >
            Disagree
          </Button>
          {failed && <Typography variant="caption" color="error" alignSelf="center">{failed}</Typography>}
        </Stack>
      )}
    </Paper>
  )
}

// Copy only: prompt changes are made by hand in the canvas editor, which keeps version history
function Improvement({ improvement }) {
  const [state, setState] = useState(null)

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(
        `${improvement.agent_name}\n\nCurrent prompt text:\n${improvement.prompt_excerpt}\n\nSuggested change:\n${improvement.proposed_change}`
      )
      setState('copied')
    } catch {
      setState('failed')
    }
  }

  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 1 }}>
      <Typography variant="caption" color="text.secondary">{improvement.agent_name}</Typography>
      <Typography variant="body2" sx={{ my: 0.5, pl: 1, borderLeft: 3, borderColor: 'divider', fontStyle: 'italic' }}>
        “{improvement.prompt_excerpt}”
      </Typography>
      <Typography variant="body2">{improvement.proposed_change}</Typography>
      <Box mt={1} display="flex" alignItems="center" gap={1}>
        <Button size="small" startIcon={<ContentCopy fontSize="small" />} onClick={copy}>
          Copy suggestion
        </Button>
        {state === 'copied' && (
          <Typography variant="caption" color="text.secondary">Copied. Make the change in the agent's canvas editor.</Typography>
        )}
        {state === 'failed' && <Typography variant="caption" color="error">Couldn't copy.</Typography>}
      </Box>
    </Paper>
  )
}

export default function TicketReviewPanel({ open, onClose, ticketId }) {
  const [review, setReview] = useState(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)

  useEffect(() => {
    if (!open || !ticketId) return undefined
    // Drop a slow response once the dialog closes or the ticket changes
    let current = true
    setLoading(true)
    setError(null)
    setReview(null)
    disputeReviewAPI
      .getTicketReview(ticketId)
      .then((result) => current && setReview(result))
      .catch((err) => {
        if (!current) return
        setError(
          err?.response?.status === 404
            ? "This ticket hasn't been reviewed yet. Finished dispute tickets are reviewed each morning."
            : 'Failed to load the review.'
        )
      })
      .finally(() => current && setLoading(false))
    return () => {
      current = false
    }
  }, [open, ticketId])

  const overall = review && (OVERALL[review.overall] || { label: review.overall, color: 'default' })
  const findings = review?.findings || []

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>AI review</DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}
        {error && <Alert severity="info">{error}</Alert>}
        {review && (
          <>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Chip label={overall.label} color={overall.color} />
              <Typography variant="caption" color="text.secondary">
                Reviewed {new Date(review.reviewed_at).toLocaleString()}
                {review.model_name && ` · ${review.model_name}`}
              </Typography>
            </Box>
            {STATUS_NOTES[review.llm_status] && (
              <Alert severity="warning" sx={{ mb: 1 }}>{STATUS_NOTES[review.llm_status]}</Alert>
            )}
            {review.summary && <Typography variant="body1" sx={{ mb: 2 }}>{review.summary}</Typography>}

            {findings.length === 0 && <Alert severity="success">No problems found.</Alert>}
            {SEVERITY_ORDER.map((severity) =>
              findings
                .filter((f) => f.severity === severity)
                .map((f) => (
                  <FindingCard
                    key={f.id}
                    finding={f}
                    agentName={review.segments?.[f.segment_id] || f.segment_id}
                    ticketId={review.ticket_id}
                    reviewedAt={review.reviewed_at}
                  />
                ))
            )}

            {review.could_not_verify?.length > 0 && (
              <Box mt={2}>
                <Typography variant="subtitle2" color="text.secondary">Couldn't verify</Typography>
                {review.could_not_verify.map((item, i) => (
                  <Typography key={i} variant="body2" color="text.secondary">
                    • {item.about} <em>({item.blocked_by})</em>
                  </Typography>
                ))}
              </Box>
            )}

            {review.improvements?.length > 0 && (
              <Box mt={2}>
                <Divider sx={{ mb: 1 }} />
                <Typography variant="subtitle2" gutterBottom>Suggested prompt improvements</Typography>
                {review.improvements.map((imp, i) => (
                  <Improvement key={i} improvement={imp} />
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
    </Dialog>
  )
}
